import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { satisfaction as api } from '../services/api';
import SatisfactionQuestionList from './SatisfactionQuestionList';
import SatisfactionSimulation from './SatisfactionSimulation';
import './SatisfactionFormulaireEditor.css';

/**
 * Éditeur d'un formulaire de satisfaction (refonte du 2026-10-05).
 *
 * L'ancienne modale empilait cinq sections où se mêlaient ce que voit le client
 * (questions) et la mécanique d'envoi (canal rangé sous « Formulaire », mode
 * caché dans « Fréquence »). Elle est désormais coupée en deux familles :
 *   - Questionnaire : ce que voit le client (questions, avis Google) ;
 *   - Envoi : qui le reçoit, quand et comment (destinataires, calendrier, message).
 * L'état (Off / Observation / Actif) est en tête, avec une phrase qui résume
 * tout le paramétrage ; un aperçu suit l'étape affichée.
 */

const JOURS = [
  { iso: 1, label: 'Lun', long: 'lundi' }, { iso: 2, label: 'Mar', long: 'mardi' },
  { iso: 3, label: 'Mer', long: 'mercredi' }, { iso: 4, label: 'Jeu', long: 'jeudi' },
  { iso: 5, label: 'Ven', long: 'vendredi' }, { iso: 6, label: 'Sam', long: 'samedi' },
  { iso: 7, label: 'Dim', long: 'dimanche' },
];

const MODES = [
  { cle: 'off', label: 'Off', classe: 'sfe-dot-off', phrase: 'le planificateur ne passe pas' },
  { cle: 'simulation', label: 'Observation', classe: 'sfe-dot-obs', phrase: 'le planificateur calcule et consigne dans Réponses, rien ne part' },
  { cle: 'actif', label: 'Actif', classe: 'sfe-dot-on', phrase: 'les messages partent réellement' },
];

const FUSEAUX = [
  { cle: 'America/Martinique', label: 'Antilles' },
  { cle: 'Europe/Paris', label: 'Métropole' },
];

const CANAUX = [
  { cle: 'sms', titre: '📱 SMS', court: 'SMS', detail: '160 caractères maximum' },
  { cle: 'email', titre: '✉️ E-mail', court: 'e-mail', detail: 'Objet et corps libres' },
  { cle: 'sms_email', titre: '📱 + ✉️ Les deux', court: 'SMS et e-mail', detail: 'Les deux si possible, sinon celui qui marche' },
];

// Présentation des sources : le libellé technique du catalogue reste la
// référence, on n'y ajoute qu'une phrase lisible.
const SOURCES_INFO = {
  a97_sellens_ventes_vn: { titre: 'Ventes VN SELLENS', detail: 'Acheteurs, après la date de vente', verbe: 'vendus' },
  crm_passages_atelier: { titre: 'Passages atelier', detail: 'Clients, après la facture atelier', verbe: 'facturés' },
  a97_sellens_passages_atelier: { titre: 'Passages atelier SELLENS', detail: 'Clients atelier SELLENS, après la facture', verbe: 'facturés' },
  manuel: { titre: 'Envoi à la main', detail: 'Depuis le CRM, client par client' },
  csv: { titre: 'Fichier CSV', detail: 'Liste déposée' },
};

// Les sources automatiques ont besoin d'un paramétrage propre ; les deux autres
// n'ont pas de ciblage (envoi déclenché à la main).
const SOURCES_AUTOMATIQUES = ['crm_passages_atelier', 'a97_sellens_ventes_vn', 'a97_sellens_passages_atelier'];
// Sources SELLENS (a97_data) : ciblage par code société SELLENS.
const SOURCES_SELLENS = ['a97_sellens_ventes_vn', 'a97_sellens_passages_atelier'];

const ETAPES = [
  { cle: 'questions', famille: 'q', icone: '✎', titre: 'Questions' },
  { cle: 'google', famille: 'q', icone: '★', titre: 'Avis Google' },
  { cle: 'destinataires', famille: 'e', icone: '👥', titre: 'Destinataires' },
  { cle: 'calendrier', famille: 'e', icone: '◷', titre: 'Calendrier' },
  { cle: 'message', famille: 'e', icone: '✉', titre: 'Message' },
];

const listeJours = (isos) => {
  const j = [...(isos || [])].sort((a, b) => a - b);
  if (!j.length) return 'aucun jour';
  if (j.join() === '1,2,3,4,5') return 'du lundi au vendredi';
  if (j.length === 7) return 'tous les jours';
  return `le ${j.map((i) => JOURS[i - 1].long).join(', ')}`;
};

// Jour (ISO 1-7) et heure « HH:MM » actuels dans le fuseau du formulaire.
function maintenantDans(tz) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const v = (t) => parts.find((p) => p.type === t)?.value;
  const iso = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }[v('weekday')];
  return { iso, heure: `${v('hour')}:${v('minute')}` };
}

const jourDecale = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

// Prochain passage du planificateur, en jours à partir d'aujourd'hui.
function prochainPassage(form) {
  const jours = form.frequence_jours || [];
  if (!jours.length) return null;
  const heure = String(form.heure_envoi || '10:00').slice(0, 5);
  const now = maintenantDans(form.timezone || 'America/Martinique');
  for (let n = 0; n < 8; n += 1) {
    const iso = ((now.iso - 1 + n) % 7) + 1;
    if (jours.includes(iso) && (n > 0 || now.heure < heure)) return { n, heure };
  }
  return null;
}

export default function SatisfactionFormulaireEditor({ formulaireId, catalogue, user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [concession, setConcession] = useState(null);
  const [initial, setInitial] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [etape, setEtape] = useState('questions');
  const [apercu, setApercu] = useState(null);
  const [volumes, setVolumes] = useState(null);
  const [calculVolumes, setCalculVolumes] = useState(false);
  const [simulation, setSimulation] = useState(false);
  const compteurCle = useRef(0);
  const smsRef = useRef(null);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getFormulaire(formulaireId);
      const qs = data.questions.map((q) => ({ ...q }));
      setForm(data.formulaire);
      setQuestions(qs);
      setConcession(data.concession || null);
      setInitial(JSON.stringify([data.formulaire, qs]));
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [formulaireId]);

  useEffect(() => { charger(); }, [charger]);

  const modifie = form && initial !== JSON.stringify([form, questions]);

  const maj = (patch) => setForm((f) => ({ ...f, ...patch }));
  const majParams = (patch) => setForm((f) => ({ ...f, source_params: { ...(f.source_params || {}), ...patch } }));

  const ajouterQuestion = () => {
    compteurCle.current += 1;
    setQuestions((qs) => [...qs, {
      _cle: `neuve-${compteurCle.current}`, libelle: '', type: 'oui_non', obligatoire: true, aide: '',
    }]);
  };

  const enregistrer = async () => {
    if (questions.some((q) => !String(q.libelle || '').trim())) {
      setErreur('Chaque question doit avoir un intitulé.');
      setEtape('questions');
      return;
    }
    setSaving(true);
    setErreur('');
    try {
      await api.updateFormulaire(form.id, {
        ...form,
        updated_at: form.updated_at,
        questions: questions.map((q) => ({
          id: q.id, libelle: q.libelle, aide: q.aide || null, type: q.type, obligatoire: q.obligatoire !== false,
        })),
      });
      await charger();
      setVolumes(null);
      if (onSaved) onSaved();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const fermer = () => {
    // eslint-disable-next-line no-alert
    if (modifie && !window.confirm('Fermer sans enregistrer les modifications ?')) return;
    onClose();
  };

  // Aperçu des messages rendu PAR LE SERVEUR (mêmes fonctions que l'envoi),
  // relancé 400 ms après la dernière frappe.
  const modeleSms = form?.modele_sms;
  const modeleObjet = form?.modele_email_objet;
  const modeleCorps = form?.modele_email_corps;
  useEffect(() => {
    if (!form?.id || etape !== 'message') return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.apercu(form.id, {
          modele_sms: modeleSms, modele_email_objet: modeleObjet, modele_email_corps: modeleCorps,
        });
        setApercu(data);
      } catch (e) {
        setApercu(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form?.id, etape, modeleSms, modeleObjet, modeleCorps]);

  const calculerVolumes = async () => {
    setCalculVolumes(true);
    try {
      const { data } = await api.simuler(form.id);
      const motifs = {};
      (data.lignes || []).filter((l) => l.etat === 'ecarte' && l.motif)
        .forEach((l) => { motifs[l.motif] = (motifs[l.motif] || 0) + 1; });
      setVolumes({
        total: data.total, retenus: data.simules, ecartes: data.ecartes, blocage: data.blocage,
        motifs: Object.entries(motifs).sort((a, b) => b[1] - a[1]).slice(0, 4),
      });
    } catch (e) {
      setVolumes({ erreur: e.response?.data?.error || 'Calcul impossible' });
    } finally {
      setCalculVolumes(false);
    }
  };

  // Insertion d'une variable à l'emplacement du curseur du SMS.
  const insererVariable = (cle) => {
    const ta = smsRef.current;
    const texte = form.modele_sms || '';
    const pos = ta && document.activeElement === ta ? ta.selectionStart : texte.length;
    maj({ modele_sms: `${texte.slice(0, pos)}{{${cle}}}${texte.slice(pos)}` });
  };

  const etat = useMemo(() => {
    if (!form) return {};
    const auto = SOURCES_AUTOMATIQUES.includes(form.source);
    const smsVoulu = form.canal === 'sms' || form.canal === 'sms_email';
    return {
      questions: questions.length > 0 && questions.every((q) => String(q.libelle || '').trim())
        ? 'ok' : 'alerte',
      google: 'ok',
      destinataires: form.source === 'crm_passages_atelier' && !(form.source_params?.codes || []).length
        ? 'alerte' : 'ok',
      calendrier: auto && !(form.frequence_jours || []).length ? 'alerte' : 'ok',
      message: smsVoulu && concession && !concession.sms_possible ? 'alerte' : 'ok',
    };
  }, [form, questions, concession]);

  if (loading || !form) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">
            {erreur ? <div className="status-error sat-bandeau">{erreur}</div> : <div className="sat-vide">Chargement…</div>}
          </div>
          {erreur && <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Fermer</button></div>}
        </div>
      </div>
    );
  }

  const sourceAutomatique = SOURCES_AUTOMATIQUES.includes(form.source);
  const infoSource = SOURCES_INFO[form.source] || { titre: form.source, detail: '' };
  const delai = form.source_params?.delai_jours ?? 3;
  const rattrapage = form.source_params?.rattrapage_jours ?? 7;
  const fuseau = FUSEAUX.find((f) => f.cle === form.timezone)?.label || form.timezone;
  const heure = String(form.heure_envoi || '10:00').slice(0, 5);
  const mode = MODES.find((m) => m.cle === form.mode) || MODES[0];
  const canal = CANAUX.find((c) => c.cle === form.canal) || CANAUX[0];
  const smsVoulu = form.canal === 'sms' || form.canal === 'sms_email';
  const emailVoulu = form.canal === 'email' || form.canal === 'sms_email';
  const modeVerrouille = !isSuperAdmin && form.mode !== 'actif';

  const sousTitre = {
    questions: `${questions.length} question${questions.length > 1 ? 's' : ''}`,
    google: form.seuil_google === null || form.seuil_google === undefined
      ? 'Aucune invitation' : `Invitation dès ${form.seuil_google}/10`,
    destinataires: infoSource.titre,
    calendrier: sourceAutomatique
      ? `${(form.frequence_jours || []).length ? JOURS.filter((j) => form.frequence_jours.includes(j.iso)).map((j) => j.label).join(' ') : 'Aucun jour'} · ${heure}`
      : 'Sans objet (envoi à la main)',
    message: canal.court.charAt(0).toUpperCase() + canal.court.slice(1),
  };

  // ── Phrase de synthèse ────────────────────────────────────────────────────
  // Le canal qui partira VRAIMENT : un SMS demandé sur une concession sans SMS
  // n'est pas annoncé comme s'il allait partir.
  const smsImpossible = smsVoulu && concession && !concession.sms_possible;
  const canalEffectif = !smsImpossible ? <b>{canal.court}</b>
    : form.canal === 'sms' ? <><b>SMS</b> (non configuré : rien ne part)</>
      : <><b>e-mail seul</b> (SMS non configuré)</>;
  const synthese = (
    <>
      {!form.actif ? <b>Hors service</b> : <b>{mode.label}</b>}
      {' : '}
      {!form.actif ? 'aucun envoi, même à la main' : mode.phrase}.{' '}
      {sourceAutomatique ? (
        <>
          Clients <b>{infoSource.titre}</b>, <b>{delai} à {delai + rattrapage} jours</b> après
          {form.source === 'a97_sellens_ventes_vn' ? ' la vente' : ' la facture'}, par {canalEffectif},{' '}
          <b>{listeJours(form.frequence_jours)}</b> à {heure} ({fuseau}).
        </>
      ) : (
        <>Envoi déclenché à la main ({infoSource.titre.toLowerCase()}), par {canalEffectif}.</>
      )}
    </>
  );

  // ── Aperçu (colonne de droite) ────────────────────────────────────────────
  const renduApercu = () => {
    if (etape === 'questions' || etape === 'google') {
      return (
        <>
          <h5>Ce que voit le client</h5>
          <div className="sfe-phone">
            <div className="sfe-screen">
              <div className="sfe-screen-logo">{concession?.nom || ''}</div>
              <div className="sfe-screen-titre">{form.message_accueil || 'Votre avis compte pour nous'}</div>
              {questions.map((q, i) => (
                <div key={q.id || q._cle || i} className="sfe-pq">
                  <b>{q.libelle || <span className="sfe-muet">Question sans intitulé</span>}{q.obligatoire !== false ? ' *' : ''}</b>
                  {q.aide && <small>{q.aide}</small>}
                  {q.type === 'nps' && <div className="sfe-nps">{[...Array(11)].map((_, n) => <span key={n}>{n}</span>)}</div>}
                  {q.type === 'oui_non' && <div className="sfe-yn"><span>Oui</span><span>Non</span></div>}
                  {q.type === 'note_5' && <div className="sfe-etoiles">★★★★★</div>}
                  {q.type === 'texte' && <div className="sfe-ta" />}
                  {q.type === 'recontact' && (
                    <div className="sfe-recontact">
                      <span>☐ Par téléphone</span><span>☐ Par SMS</span><span>☐ Par e-mail</span>
                      <small>Textes fournis par A97 RGPD au nom de la société</small>
                    </div>
                  )}
                </div>
              ))}
              <div className="sfe-envoyer">Envoyer</div>
            </div>
          </div>
          <h5>Après l'envoi</h5>
          <div className="sfe-merci">
            {form.message_remerciement || 'Merci pour votre réponse !'}
            {form.seuil_google !== null && form.seuil_google !== undefined && (
              <div className="sfe-google">★ Laisser un avis Google <small>(note ≥ {form.seuil_google}/10)</small></div>
            )}
          </div>
        </>
      );
    }
    if (etape === 'message') {
      const smsCoupe = smsVoulu && concession && !concession.sms_possible;
      // Règle d'envoi en clair : c'est celle de satisfaction-envois.trier.
      let regle;
      if (form.canal === 'sms') {
        regle = smsCoupe
          ? <>Le client ne recevrait <b>rien</b> : le SMS n'est pas configuré sur cette concession.</>
          : <>Le client reçoit <b>uniquement le SMS</b>. Sans mobile, il n'est pas sollicité.</>;
      } else if (form.canal === 'email') {
        regle = <>Le client reçoit <b>uniquement l'e-mail</b>. Sans adresse e-mail, il n'est pas sollicité.</>;
      } else {
        regle = smsCoupe
          ? <>SMS non configuré sur cette concession : le client reçoit <b>uniquement l'e-mail</b>.</>
          : <>Le client reçoit <b>le SMS et l'e-mail</b> s'il a un mobile et une adresse ; sinon, celui des deux qui est possible.</>;
      }
      return (
        <>
          <h5>Ce que reçoit le client (exemple)</h5>
          <div className={`sfe-regle ${smsCoupe ? 'sfe-regle-alerte' : ''}`}>{regle}</div>
          {!apercu && <div className="sat-vide">Calcul de l'aperçu…</div>}
          {apercu && smsVoulu && (
            <div className={`sfe-canal ${smsCoupe ? 'sfe-canal-coupe' : ''}`}>
              <div className="sfe-canal-titre">
                📱 SMS
                <span className={apercu.sms_longueur > 160 ? 'sfe-ko' : ''}>{apercu.sms_longueur}/160</span>
                {smsCoupe && <span className="sfe-canal-badge">ne partira pas</span>}
              </div>
              <div className="sfe-bulle">{apercu.sms}</div>
            </div>
          )}
          {apercu && emailVoulu && (
            <div className="sfe-canal">
              <div className="sfe-canal-titre">✉️ E-mail</div>
              <div className="sfe-mail">
                <div className="sfe-mail-objet"><span className="sfe-muet">Objet :</span> {apercu.email_objet}</div>
                {/* HTML produit par satisfaction-messages.corpsHtml : variables échappées, seul le lien est une ancre. */}
                <div dangerouslySetInnerHTML={{ __html: apercu.email_corps }} />
              </div>
            </div>
          )}
          <p className="sat-aide">Prénom, marque et point de vente d'exemple : ceux du client les remplacent à l'envoi.</p>
        </>
      );
    }
    // Destinataires / calendrier
    if (!sourceAutomatique) {
      return (
        <>
          <h5>Fonctionnement</h5>
          <p className="sat-aide">
            Pas de planificateur pour cette source : le questionnaire part quand quelqu'un le
            déclenche depuis le CRM{form.source === 'csv' ? ' ou dépose un fichier' : ''}.
          </p>
        </>
      );
    }
    const prochain = prochainPassage(form);
    const debut = -(delai + rattrapage);
    const fin = -delai;
    return (
      <>
        <h5>Au prochain passage</h5>
        <div className="sfe-timeline">
          <div>
            <b>{prochain ? `${prochain.n === 0 ? "Aujourd'hui" : jourDecale(prochain.n)} à ${prochain.heure}` : 'Aucun passage prévu'}</b>
            <small>{prochain ? `heure ${fuseau === 'Antilles' ? 'des Antilles' : 'de Métropole'}` : 'Choisissez au moins un jour'}</small>
          </div>
          {prochain && (
            <div>
              <b>{infoSource.verbe === 'vendus' ? 'Ventes' : 'Factures'} du {jourDecale(debut + prochain.n)} au {jourDecale(fin + prochain.n)}</b>
              <small>{delai} à {delai + rattrapage} jours avant le passage</small>
            </div>
          )}
          <div>
            <b>Jamais deux fois</b>
            <small>un client sollicité ne l'est plus pendant {form.source_params?.anti_doublon_mois ?? 6} mois</small>
          </div>
        </div>
        {isSuperAdmin && (
          <>
            <h5>Volumes</h5>
            {!volumes && (
              <button className="btn-secondary" onClick={calculerVolumes} disabled={calculVolumes}>
                {calculVolumes ? 'Calcul…' : "📊 Calculer pour aujourd'hui"}
              </button>
            )}
            {volumes?.erreur && <div className="status-error sat-bandeau">{volumes.erreur}</div>}
            {volumes && !volumes.erreur && (
              <>
                {volumes.blocage && <div className="status-warning sat-bandeau">{volumes.blocage}</div>}
                <div className="sfe-chiffres">
                  <div><b>{volumes.total}</b><small>dans la fenêtre</small></div>
                  <div><b>{volumes.retenus}</b><small>seraient sollicités</small></div>
                </div>
                {volumes.motifs.map(([motif, n]) => (
                  <div key={motif} className="sfe-motif"><span>{motif}</span><b>{n}</b></div>
                ))}
                <button className="btn-secondary" onClick={() => setSimulation(true)}>Voir le détail</button>
              </>
            )}
            {modifie && (
              <p className="sat-aide">Calculé sur la version <b>enregistrée</b> du formulaire.</p>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content large sfe" onClick={(e) => e.stopPropagation()}>
          {/* ── En-tête : identité, état, synthèse ── */}
          <div className="modal-header sfe-header">
            <input className="sfe-nom" value={form.nom} aria-label="Nom du formulaire"
              onChange={(e) => maj({ nom: e.target.value })} />
            <label className="sfe-service" title="Hors service : aucun envoi, même à la main">
              <input type="checkbox" checked={!!form.actif} onChange={(e) => maj({ actif: e.target.checked })} />
              En service
            </label>
            <div className="header-segmented sfe-etat" role="group" aria-label="État du planificateur"
              title={modeVerrouille ? 'Seul un super-admin change cet état' : undefined}>
              {MODES.map((m) => (
                <button key={m.cle} type="button"
                  className={`header-segmented-btn ${form.mode === m.cle ? 'active' : ''}`}
                  disabled={modeVerrouille || (m.cle === 'actif' && !isSuperAdmin)}
                  onClick={() => maj({ mode: m.cle })}>
                  <span className={`sfe-dot ${m.classe}`} />{m.label}
                </button>
              ))}
            </div>
            <button className="modal-close" onClick={fermer}>✕</button>
            <div className="sfe-synthese">{synthese}</div>
          </div>

          <div className="sfe-corps">
            {/* ── Navigation ── */}
            <nav className="sfe-nav">
              {[['q', 'Questionnaire', 'Ce que voit le client'], ['e', 'Envoi', 'Qui le reçoit, quand, comment']].map(([fam, titre, sous]) => (
                <div key={fam} className={`sfe-famille sfe-f-${fam}`}>
                  <h4>{titre}</h4>
                  <p>{sous}</p>
                  {ETAPES.filter((e) => e.famille === fam).map((e) => (
                    <button key={e.cle} type="button"
                      className={`sfe-etape ${etape === e.cle ? 'sfe-etape-active' : ''}`}
                      onClick={() => setEtape(e.cle)}>
                      <span className="sfe-ic">{e.icone}</span>
                      <span className="sfe-etape-txt"><strong>{e.titre}</strong><small>{sousTitre[e.cle]}</small></span>
                      {etat[e.cle] === 'alerte'
                        ? <span className="sfe-alerte" title="À compléter">⚠</span>
                        : <span className="sfe-ok">✓</span>}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            {/* ── Contenu de l'étape ── */}
            <div className="sfe-contenu">
              {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

              {etape === 'questions' && (
                <>
                  <EnTete famille="q" titre="Questions" sous="La page qui s'ouvre quand le client clique sur le lien." />
                  <div className="sfe-bloc">
                    <label className="sat-champ">
                      <span>Titre d'accueil</span>
                      <input className="sat-input" value={form.message_accueil || ''}
                        placeholder="Votre avis compte pour nous"
                        onChange={(e) => maj({ message_accueil: e.target.value })} />
                    </label>
                  </div>
                  <div className="sfe-bloc">
                    <h3>Questions</h3>
                    <p className="sfe-sous">Glissez ⠿ pour réordonner. La note sur 10 sert de NPS : c'est elle qui signale les clients mécontents.</p>
                    <SatisfactionQuestionList
                      questions={questions}
                      types={catalogue?.types_question || []}
                      onChange={setQuestions}
                    />
                    <button type="button" className="sfe-ajout" onClick={ajouterQuestion}>+ Ajouter une question</button>
                  </div>
                  <div className="sfe-bloc">
                    <label className="sat-champ">
                      <span>Message de remerciement</span>
                      <input className="sat-input" value={form.message_remerciement || ''}
                        placeholder="Merci pour votre réponse !"
                        onChange={(e) => maj({ message_remerciement: e.target.value })} />
                    </label>
                  </div>
                </>
              )}

              {etape === 'google' && (
                <>
                  <EnTete famille="q" titre="Avis Google" sous="Après sa réponse, un client très satisfait est invité à publier un avis." />
                  <div className="sfe-bloc">
                    <label className="sat-champ">
                      <span>Inviter à partir de la note (sur 10)</span>
                      <select className="sat-input"
                        value={form.seuil_google === null || form.seuil_google === undefined ? '' : String(form.seuil_google)}
                        onChange={(e) => maj({ seuil_google: e.target.value === '' ? null : Number(e.target.value) })}>
                        <option value="">Aucune invitation</option>
                        {[6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}/10 et plus</option>)}
                      </select>
                    </label>
                    <p className="sat-aide">
                      L'invitation apparaît sur la page de remerciement, uniquement si le questionnaire
                      contient une note sur 10. Publier un avis demande un compte Google au client.
                    </p>
                  </div>
                </>
              )}

              {etape === 'destinataires' && (
                <>
                  <EnTete famille="e" titre="Destinataires" sous="D'où viennent les clients sollicités." />
                  <div className="sfe-bloc">
                    <h3>Source</h3>
                    <div className="sfe-tuiles">
                      {(catalogue?.sources || []).map((s) => {
                        const info = SOURCES_INFO[s.cle] || { titre: s.label, detail: '' };
                        return (
                          <button key={s.cle} type="button" title={s.label}
                            className={`sfe-tuile ${form.source === s.cle ? 'sfe-tuile-active' : ''}`}
                            onClick={() => maj({ source: s.cle })}>
                            <strong>{info.titre}</strong><small>{info.detail}</small>
                          </button>
                        );
                      })}
                    </div>
                    {form.source === 'crm_passages_atelier' && (
                      <label className="sat-champ">
                        <span>Codes société Datacar (séparés par des virgules)</span>
                        <input className="sat-input" value={(form.source_params?.codes || []).join(', ')}
                          placeholder="obligatoire : sans code, aucun destinataire"
                          onChange={(e) => majParams({ codes: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) })} />
                      </label>
                    )}
                    {SOURCES_SELLENS.includes(form.source) && (
                      <>
                        <label className="sat-champ">
                          <span>Codes société SELLENS (vide = les 19)</span>
                          <input className="sat-input" value={(form.source_params?.codsoc || []).join(', ')}
                            placeholder="ex. 001, 004, 010"
                            onChange={(e) => majParams({ codsoc: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) })} />
                        </label>
                        {form.source === 'a97_sellens_ventes_vn' ? (
                          <p className="sat-aide">
                            Coordonnées reprises de l'export des ventes VN SELLENS : environ trois acheteurs
                            sur quatre sont joignables. Les autres sont écartés avec le motif « aucun contact
                            connu », visible dans la simulation.
                          </p>
                        ) : (
                          <p className="sat-aide">
                            Une ligne par facture atelier client (main-d'œuvre et n° d'OR) : les cessions
                            internes et le comptoir pièces sont exclus. Environ neuf clients sur dix sont
                            joignables. L'export SELLENS ne donne pas le modèle du véhicule : {'{{modele}}'} reste vide.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {sourceAutomatique && (
                    <div className="sfe-bloc">
                      <h3>Règles</h3>
                      <div className="sfe-phrase">
                        Solliciter les clients <b>{infoSource.verbe}</b> il y a{' '}
                        <NombreEnLigne valeur={delai} min={0} max={365}
                          onChange={(v) => majParams({ delai_jours: v })} label="Délai minimum (jours)" />
                        {' '}à{' '}
                        <NombreEnLigne valeur={delai + rattrapage} min={delai} max={delai + 365}
                          onChange={(v) => majParams({ rattrapage_jours: Math.max(0, v - delai) })} label="Délai maximum (jours)" />
                        {' '}jours, au plus{' '}
                        <NombreEnLigne valeur={form.source_params?.quota_jour ?? 200} min={1} max={100000}
                          onChange={(v) => majParams({ quota_jour: v })} label="Quota par jour" />
                        {' '}par jour, et jamais deux fois en moins de{' '}
                        <NombreEnLigne valeur={form.source_params?.anti_doublon_mois ?? 6} min={1} max={60}
                          onChange={(v) => majParams({ anti_doublon_mois: v })} label="Délai avant de resolliciter (mois)" />
                        {' '}mois.
                      </div>
                      <details className="sat-aide sfe-details">
                        <summary>Pourquoi une fenêtre de jours et pas un jour précis ?</summary>
                        Le délai est compté depuis la facturation : la date de vente pour un véhicule,
                        la date de facture pour un passage atelier. Un client dont le jour cible tombe un
                        week-end, ou un jour où le serveur n'a pas tourné, serait perdu : avec un envoi du
                        lundi au vendredi, <b>17,6 % des clients ne seraient jamais contactés</b>. La
                        fenêtre les rattrape, sans doublon puisqu'un client déjà sollicité ne l'est jamais
                        deux fois.
                      </details>
                    </div>
                  )}
                </>
              )}

              {etape === 'calendrier' && (
                <>
                  <EnTete famille="e" titre="Calendrier" sous="Quand le planificateur passe." />
                  {!sourceAutomatique ? (
                    <div className="sfe-bloc">
                      <p className="sat-aide">
                        Sans objet pour la source « {infoSource.titre} » : rien ne part automatiquement.
                      </p>
                    </div>
                  ) : (
                    <div className="sfe-bloc">
                      <h3>Jours de passage</h3>
                      <div className="sfe-jours">
                        {JOURS.map((j) => {
                          const actif = (form.frequence_jours || []).includes(j.iso);
                          return (
                            <button key={j.iso} type="button" aria-pressed={actif}
                              className={`sfe-jour ${actif ? 'sfe-jour-actif' : ''}`}
                              onClick={() => maj({
                                frequence_jours: actif
                                  ? (form.frequence_jours || []).filter((x) => x !== j.iso)
                                  : [...(form.frequence_jours || []), j.iso].sort((a, b) => a - b),
                              })}>{j.label}</button>
                          );
                        })}
                      </div>
                      <div className="sat-grille">
                        <label className="sat-champ">
                          <span>Heure</span>
                          <input className="sat-input" type="time" value={heure}
                            onChange={(e) => maj({ heure_envoi: e.target.value })} />
                        </label>
                        <label className="sat-champ">
                          <span>Fuseau horaire</span>
                          <select className="sat-input" value={form.timezone} onChange={(e) => maj({ timezone: e.target.value })}>
                            {FUSEAUX.map((f) => <option key={f.cle} value={f.cle}>{f.label}</option>)}
                          </select>
                        </label>
                      </div>
                      <p className="sat-aide">
                        L'état en haut de la fenêtre décide de ce qui se passe à chaque passage : rien
                        (Off), un calcul consigné dans Réponses sans envoi (Observation), ou l'envoi réel
                        (Actif). Passez par l'observation avant d'activer.
                      </p>
                    </div>
                  )}
                </>
              )}

              {etape === 'message' && (
                <>
                  <EnTete famille="e" titre="Message" sous="Le texte qui porte le lien du questionnaire." />
                  <div className="sfe-bloc">
                    <h3>Canal</h3>
                    <div className="sfe-tuiles">
                      {CANAUX.map((c) => (
                        <button key={c.cle} type="button"
                          className={`sfe-tuile ${form.canal === c.cle ? 'sfe-tuile-active' : ''}`}
                          onClick={() => maj({ canal: c.cle })}>
                          <strong>{c.titre}</strong><small>{c.detail}</small>
                        </button>
                      ))}
                    </div>
                    {smsVoulu && concession && !concession.sms_possible && (
                      <div className="status-warning sat-bandeau">
                        ⚠️ SMS non configuré sur {concession.nom || 'cette concession'} (Administration ▸
                        Concessions) : {emailVoulu ? 'les messages partiront par e-mail seul' : 'aucun message ne partira'}.
                      </div>
                    )}
                  </div>
                  <div className="sfe-bloc">
                    <h3>Variables</h3>
                    <p className="sfe-sous">Cliquez pour insérer dans le SMS, ou tapez-les dans l'e-mail. <code>{'{{lien}}'}</code> est obligatoire.</p>
                    <div className="sat-variables">
                      {(catalogue?.variables || []).map((v) => (
                        <button key={v.cle} type="button" className="filter-chip chip-default" title={v.label}
                          onClick={() => insererVariable(v.cle)}>{`{{${v.cle}}}`}</button>
                      ))}
                    </div>
                  </div>
                  {smsVoulu && (
                    <div className="sfe-bloc">
                      <h3>SMS</h3>
                      <textarea ref={smsRef} className="sat-input" rows={3} value={form.modele_sms || ''}
                        placeholder={catalogue?.modeles_defaut?.sms}
                        onChange={(e) => maj({ modele_sms: e.target.value })} />
                    </div>
                  )}
                  {emailVoulu && (
                    <div className="sfe-bloc">
                      <h3>E-mail</h3>
                      <label className="sat-champ">
                        <span>Objet</span>
                        <input className="sat-input" value={form.modele_email_objet || ''}
                          placeholder={catalogue?.modeles_defaut?.email_objet}
                          onChange={(e) => maj({ modele_email_objet: e.target.value })} />
                      </label>
                      <label className="sat-champ">
                        <span>Corps</span>
                        <textarea className="sat-input" rows={7} value={form.modele_email_corps || ''}
                          placeholder={catalogue?.modeles_defaut?.email_corps}
                          onChange={(e) => maj({ modele_email_corps: e.target.value })} />
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Aperçu ── */}
            <aside className="sfe-apercu">{renduApercu()}</aside>
          </div>

          <div className="modal-footer">
            {modifie && <span className="sfe-modifie">● Modifications non enregistrées</span>}
            {isSuperAdmin && (
              <button className="btn-secondary" onClick={() => setSimulation(true)}
                title={modifie ? 'La simulation porte sur la version enregistrée' : undefined}>🧪 Simuler aujourd'hui</button>
            )}
            <button className="btn-secondary" onClick={fermer}>Annuler</button>
            <button className="btn-primary" onClick={enregistrer} disabled={saving || !modifie}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      {simulation && <SatisfactionSimulation formulaire={form} onClose={() => setSimulation(false)} />}
    </>
  );
}

function EnTete({ famille, titre, sous }) {
  return (
    <div className="sfe-entete">
      <span className={`sfe-tag sfe-tag-${famille}`}>{famille === 'q' ? 'Questionnaire' : 'Envoi'}</span>
      <h2>{titre}</h2>
      <p>{sous}</p>
    </div>
  );
}

// Nombre saisi au fil d'une phrase. La valeur n'est remontée que si elle est
// un entier dans les bornes : un champ vidé le temps de retaper ne doit pas
// enregistrer 0.
function NombreEnLigne({ valeur, min, max, onChange, label }) {
  const [texte, setTexte] = useState(String(valeur));
  useEffect(() => { setTexte(String(valeur)); }, [valeur]);
  return (
    <input className="sfe-nombre" type="number" inputMode="numeric" min={min} max={max}
      aria-label={label} title={label} value={texte}
      onChange={(e) => {
        setTexte(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value !== '' && Number.isInteger(n) && n >= min && n <= max) onChange(n);
      }}
      onBlur={() => setTexte(String(valeur))} />
  );
}
